import { type PropsWithChildren, useRef } from 'react';

import { createStore, type Store } from '../../../../core/store/store.js';
import { useColorRule } from '../../../core/color-rule/useColorRule.js';
import { DEFAULT_VALUE_ACCESSOR } from '../../../core/constants.js';
import type {
  ColorPalette,
  CustomColorPalette,
} from '../../../core/types/color-palette.js';
import { INITIAL_STATE } from '../constants.js';
import { MultiMeterBarChartStore } from '../context/MultiMeterBarChartStore.js';
import { useMultiMeterBarStore } from '../hooks/useMultiMeterBarStore.js';
import type {
  MultiMeterBarChartData,
  MultiMeterBarSegmentProps,
} from '../types/multi-meter-bar-chart.js';
import type { MultiMeterBarChartState } from '../types/state.js';

export interface StoreProviderProps {
  colorPalette: ColorPalette | CustomColorPalette;
  segmentsSlot: MultiMeterBarSegmentProps[];
}

type SeriesForColorRule = {
  value: number | MultiMeterBarChartData;
  [key: string]: unknown;
};

export const StoreProvider = ({
  colorPalette,
  segmentsSlot,
  children,
}: PropsWithChildren<StoreProviderProps>) => {
  const storeRef = useRef<Store<MultiMeterBarChartState> | null>(null);

  // Create series array with metadata for ColorRule evaluation
  const seriesForColorRule = segmentsSlot.map((segment) => {
    if (typeof segment.value === 'number') {
      return {
        value: segment.value,
        name: segment.name,
      };
    }

    return {
      ...segment.value,
      name: segment.name,
      _chartValueAccessor: segment.valueAccessor ?? DEFAULT_VALUE_ACCESSOR,
    };
  }) as SeriesForColorRule[];

  const colorRules = useColorRule(seriesForColorRule, 'meterBar');

  const { initSegmentsState } = useMultiMeterBarStore();

  const { segments, visibleSegments } = initSegmentsState(
    segmentsSlot,
    seriesForColorRule,
    colorPalette,
    colorRules,
  );

  storeRef.current = createStore({
    ...INITIAL_STATE,
    segments,
    visibleSegments,
  });

  return (
    <MultiMeterBarChartStore.Provider value={storeRef.current}>
      {children}
    </MultiMeterBarChartStore.Provider>
  );
};
